import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { useEmberStore } from './lib/store';
import LoadingSpinner from './components/LoadingSpinner';
import SplashScreen from './components/SplashScreen';
import Button from './components/ui/Button';

// Pages
import Home from './pages/Home';
import Onboarding from './pages/Onboarding';
import Login from './pages/Login';
import LandingPagePreview from './pages/LandingPagePreview';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Discover from './pages/Discover';
import CheckIn from './pages/CheckIn';
import CheckIns from './pages/CheckIns';
import CreateEvent from './pages/CreateEvent';
import EditEvent from './pages/EditEvent';
import MyCellar from './pages/MyCellar';
import AddCellarItem from './pages/AddCellarItem';
import EditCellarItem from './pages/EditCellarItem';
import MapView from './pages/MapView';
import Sessions from './pages/Sessions';
import NewSession from './pages/NewSession';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Updates from './pages/Updates';
import UpdateDetail from './pages/UpdateDetail';

// Components
import InstallPrompt from './components/InstallPrompt';

function App() {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const { currentUser, setCurrentUser } = useEmberStore();
  const isE2E = import.meta.env.VITE_E2E === '1';
  const [profileError, setProfileError] = useState<string | null>(null);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setShowSplash(false), 1200);
    return () => clearTimeout(timeout);
  }, []);

  // Global safety timeout to prevent infinite loading
  useEffect(() => {
    const globalTimeout = setTimeout(() => {
      console.error('[APP] Global timeout - forcing app to load after 15 seconds');
      setLoading(false);
    }, 15000);

    return () => clearTimeout(globalTimeout);
  }, []);

  // Session health check to prevent unexpected logouts
  useEffect(() => {
    const sessionHealthCheck = setInterval(async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[APP] Session health check failed:', error);
          // Don't auto-logout, just log the error
        } else if (session) {
          console.log('[APP] Session health check passed');
        }
      } catch (err) {
        console.warn('[APP] Session health check error:', err);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(sessionHealthCheck);
  }, []);

  useEffect(() => {
    console.log('[APP] Initializing app...');
    
    if (isE2E) {
      console.log('[APP] E2E mode - seeding user');
      // Seed a minimal user profile in E2E mode so pages render
      setCurrentUser({
        id: 'e2e-user',
        username: 'e2e',
        created_at: new Date().toISOString(),
        is_profile_public: true,
      } as any);
      setSession({} as any);
      setLoading(false);
      return;
    }

    console.log('[APP] Checking for existing session...');
    // Check for existing session with timeout
    const sessionTimeout = setTimeout(() => {
      console.error('[APP] Session check timeout - forcing loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      clearTimeout(sessionTimeout);
      console.log('[APP] Session check completed:', { session: !!session, error });
      setSession(session);
      if (session?.user) {
        console.log('[APP] User found, loading profile...');
        setLoading(true);
        loadUserProfile(session.user.id);
      } else {
        console.log('[APP] No user, setting loading to false');
        setProfileError(null);
        setLoading(false);
      }
    }).catch((error) => {
      clearTimeout(sessionTimeout);
      console.error('[APP] Session check failed:', error);
      setProfileError(error instanceof Error ? error.message : 'Failed to check session');
      setLoading(false);
    });

    console.log('[APP] Setting up auth state listener...');
    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[APP] Auth state changed:', { event, session: !!session });
      
      // Only handle explicit sign out, not token refresh failures
      if (event === 'SIGNED_OUT' && !session) {
        console.log('[APP] User explicitly signed out');
        setSession(null); // Clear session state for proper routing
        setCurrentUser(null);
        setProfileError(null);
        setLoading(false);
        return;
      }
      
      // Handle token refresh failures gracefully - don't log out user
      if (event === 'TOKEN_REFRESHED') {
        console.log('[APP] Token refreshed successfully');
        // Don't change currentUser state on token refresh
        return;
      }
      
      setSession(session);
      if (session?.user) {
        console.log('[APP] Auth change - loading profile...');
        setLoading(true);
        loadUserProfile(session.user.id);
      } else {
        console.log('[APP] Auth change - no user, setting loading to false');
        setCurrentUser(null);
        setProfileError(null);
        setLoading(false);
      }
    });

    return () => {
      console.log('[APP] Cleaning up auth listener');
      subscription.unsubscribe();
    };
  }, [setCurrentUser]);

  async function loadUserProfile(userId: string) {
    console.log('[APP] Loading user profile for:', userId);
    setProfileError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('[APP] Profile load error:', error);
        if (error.code === 'PGRST116') {
          console.log('[APP] Profile not found - user needs onboarding');
          setCurrentUser(null);
          setProfileError(null);
        } else {
          console.error('[APP] Profile load failed:', error);
          setProfileError(error.message ?? 'Failed to load profile');
        }
        return;
      }

      if (data) {
        console.log('[APP] Profile loaded successfully');
        setCurrentUser(data);
        setProfileError(null);
      } else {
        console.log('[APP] No profile data found - user needs onboarding');
        setCurrentUser(null);
        setProfileError(null);
      }
    } catch (error) {
      console.error('[APP] Profile load exception:', error);
      setProfileError(error instanceof Error ? error.message : 'Failed to load profile');
    } finally {
      console.log('[APP] Setting loading to false');
      setLoading(false);
    }
  }

  async function handleProfileRetry() {
    if (!session?.user?.id) return;
    setLoading(true);
    await loadUserProfile(session.user.id);
  }

  async function handleProfileSignOut() {
    try {
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setCurrentUser(null);
      setProfileError(null);
      setLoading(false);
    }
  }

  let content: ReactNode;

  if (loading) {
    content = <LoadingSpinner fullPage text="Loading Ember..." />;
  } else if (session && profileError) {
    content = (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
        <div className="card" style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Can’t reach Ember</h1>
          <p style={{ color: '#cfcfcf', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            {profileError}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
            <Button onClick={handleProfileRetry} variant="primary" fullWidth>
              Try Again
            </Button>
            <Button onClick={handleProfileSignOut} variant="secondary" fullWidth>
              Log Out
            </Button>
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <BrowserRouter>
        <Routes>
          {isE2E ? (
            // E2E mode: expose authenticated routes without auth gating
            <>
              <Route path="/" element={<Home />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/checkin" element={<CheckIn />} />
              <Route path="/checkins" element={<CheckIns />} />
              <Route path="/create-event" element={<CreateEvent />} />
              <Route path="/edit-event/:eventId" element={<EditEvent />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/sessions/new" element={<NewSession />} />
              <Route path="/sessions/edit/:sessionId" element={<NewSession />} />
              <Route path="/cellar" element={<MyCellar />} />
              <Route path="/cellar/add" element={<AddCellarItem />} />
              <Route path="/cellar/edit/:type/:id" element={<EditCellarItem />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/updates/:slug" element={<UpdateDetail />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/:userId" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : !session ? (
            <>
              <Route path="/" element={<LandingPagePreview />} />
              <Route path="/login" element={<Login />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/updates/:slug" element={<UpdateDetail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : !currentUser ? (
            <>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="*" element={<Navigate to="/onboarding" replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={<Home />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/checkin" element={<CheckIn />} />
              <Route path="/checkins" element={<CheckIns />} />
              <Route path="/create-event" element={<CreateEvent />} />
              <Route path="/edit-event/:eventId" element={<EditEvent />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/sessions/new" element={<NewSession />} />
              <Route path="/sessions/edit/:sessionId" element={<NewSession />} />
              <Route path="/cellar" element={<MyCellar />} />
              <Route path="/cellar/add" element={<AddCellarItem />} />
              <Route path="/cellar/edit/:type/:id" element={<EditCellarItem />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/updates/:slug" element={<UpdateDetail />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/:userId" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
        {/* Toast notifications - appears throughout the app */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4200,
            className: 'ember-toast',
            style: {
              background: '#101010',
              color: '#f5f5f5',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.04)',
              boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55)',
              fontSize: '0.92rem',
              fontWeight: 500,
            },
          }}
        />
        {/* Show install prompt only for authenticated users */}
        {!isE2E && session && currentUser && <InstallPrompt />}
      </BrowserRouter>
    );
  }

  return (
    <>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      {content}
    </>
  );
}

export default App;

